import { useEffect, useRef } from "react";

export function GlobalBackground() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let raf;
    let w = 0;
    let h = 0;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isMobile = window.innerWidth < 768;
    const N = isMobile ? 44 : 85;

    let theme = document.documentElement.getAttribute("data-theme") || "dark";
    const applyOpacity = () => {
      canvas.style.opacity = theme === "light" ? "0.42" : "0.6";
    };
    applyOpacity();
    const mo = new MutationObserver(() => {
      theme = document.documentElement.getAttribute("data-theme") || "dark";
      applyOpacity();
    });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

    const pts = Array.from({ length: N }, () => {
      const r = 1.6 + Math.random() * 2.6;
      const th = Math.random() * Math.PI * 2;
      const ph = Math.acos(2 * Math.random() - 1);
      return {
        x: r * Math.sin(ph) * Math.cos(th),
        y: r * Math.cos(ph),
        z: r * Math.sin(ph) * Math.sin(th),
        c: Math.random(),
      };
    });

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.75);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    let t = Math.random() * 10;
    const draw = () => {
      raf = requestAnimationFrame(draw);
      if (document.hidden) return;
      t += reduced ? 0 : 0.0016;
      ctx.clearRect(0, 0, w, h);
      const cx = w / 2;
      const cy = h / 2;
      const R = Math.min(w, h) * 0.46;
      const rotY = t;
      const tilt = 0.42;
      const cosY = Math.cos(rotY);
      const sinY = Math.sin(rotY);
      const cosX = Math.cos(tilt);
      const sinX = Math.sin(tilt);
      const lineAlpha = theme === "light" ? 0.14 : 0.12;

      const proj = pts.map((p) => {
        const x = p.x * cosY + p.z * sinY;
        let z = -p.x * sinY + p.z * cosY;
        const y = p.y * cosX - z * sinX;
        z = p.y * sinX + z * cosX;
        const persp = 3.4 / (3.4 + z);
        return { sx: cx + x * R * persp, sy: cy + y * R * persp, depth: Math.max(0, 1 - (z + 4.2) / 8.4), c: p.c };
      });

      ctx.lineWidth = 0.6;
      for (let i = 0; i < proj.length; i++) {
        for (let j = i + 1; j < proj.length; j++) {
          const a = proj[i];
          const b = proj[j];
          const d = Math.hypot(a.sx - b.sx, a.sy - b.sy);
          if (d < R * 0.42) {
            ctx.strokeStyle = `rgba(120,140,180,${lineAlpha * Math.min(a.depth, b.depth)})`;
            ctx.beginPath();
            ctx.moveTo(a.sx, a.sy);
            ctx.lineTo(b.sx, b.sy);
            ctx.stroke();
          }
        }
      }

      for (const p of proj) {
        const color = p.c < 0.34 ? "255,136,0" : p.c < 0.67 ? "0,176,255" : "0,230,118";
        const alpha = (theme === "light" ? 0.35 : 0.28) + p.depth * (theme === "light" ? 0.5 : 0.55);
        ctx.beginPath();
        ctx.fillStyle = `rgba(${color},${alpha})`;
        ctx.arc(p.sx, p.sy, 0.7 + p.depth * 2.1, 0, Math.PI * 2);
        ctx.fill();
      }

      // rotating orbital rings
      const ringAlpha = theme === "light" ? 0.16 : 0.12;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(0.45 + t * 0.35);
      ctx.scale(1, 0.3);
      ctx.beginPath();
      ctx.strokeStyle = `rgba(255,136,0,${ringAlpha})`;
      ctx.lineWidth = 1;
      ctx.arc(0, 0, R * 1.15, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(-0.3 - t * 0.22);
      ctx.scale(1, 0.26);
      ctx.beginPath();
      ctx.strokeStyle = `rgba(0,176,255,${ringAlpha})`;
      ctx.lineWidth = 1;
      ctx.arc(0, 0, R * 1.45, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      mo.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      data-testid="global-3d-background"
      aria-hidden="true"
      className="fixed inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: -1 }}
    />
  );
}
